// Utilitaires de texte riche (WKF-013) : la description d'une tâche est saisie
// via <SimpleRichText> (gras/italique/souligné/listes) et stockée en HTML dans
// `notifications.message` (TEXT, pas de migration). Deux besoins :
//   1. `htmlToPlainPreview` — aperçu compact en TEXTE BRUT dans les zones denses
//      (cloche, ligne page, tooltip calendrier) : on ne rend JAMAIS de HTML brut
//      en liste (markup cassé dans un line-clamp + surface XSS).
//   2. `sanitizeRichHtml` — allowlist minimaliste au RENDU du détail
//      (dangerouslySetInnerHTML) : ne survivent que les balises de mise en forme
//      produites par SimpleRichText, aucun attribut dangereux (on*/href/src/style
//      arbitraire), donc pas de stored-XSS entre utilisateurs (assigné/délégué).
//      Aucune dépendance (pas de DOMPurify au repo) : sanitisation par le DOM natif.

/** Balises autorisées au rendu (exactement ce que produit SimpleRichText). */
const ALLOWED_TAGS = new Set([
    'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
    'UL', 'OL', 'LI', 'P', 'DIV', 'BR', 'SPAN',
])

/** Valeurs `text-align` autorisées (seul style conservé : l'alignement). */
const ALLOWED_ALIGN = new Set(['left', 'center', 'right', 'justify'])

/**
 * Convertit du HTML en texte brut lisible pour un aperçu compact.
 * - Les fins de bloc (</p>, </li>, </div>, <br>) deviennent des espaces.
 * - Les espaces sont normalisés (pas de retours multiples dans un line-clamp).
 * SSR-safe : repli regex si `document` indisponible.
 */
export function htmlToPlainPreview(html: string | null | undefined): string {
    if (!html) return ''
    // Pas de balise ? renvoyer tel quel (message texte simple existant).
    if (!/[<&]/.test(html)) return html.trim()
    if (typeof document === 'undefined') {
        return html
            .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' ')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    }
    const tpl = document.createElement('template')
    tpl.innerHTML = html
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '$& ')
        .replace(/<br\s*\/?>/gi, ' ')
    const text = tpl.content.textContent ?? ''
    return text.replace(/\s+/g, ' ').trim()
}

/**
 * Sanitise du HTML de mise en forme par allowlist (balises + alignement).
 * Toute balise non autorisée est « dépliée » (contenu conservé, balise retirée)
 * plutôt que supprimée, pour ne pas perdre de texte. Tous les attributs sont
 * retirés sauf `style="text-align:…"`. Aucune dépendance externe.
 */
export function sanitizeRichHtml(html: string | null | undefined): string {
    if (!html) return ''
    if (typeof document === 'undefined') return '' // rendu HTML côté client uniquement
    const tpl = document.createElement('template')
    tpl.innerHTML = html
    sanitizeNode(tpl.content)
    return tpl.innerHTML
}

function sanitizeNode(root: Node): void {
    // Parcours en profondeur ; on collecte d'abord pour muter sans casser l'itération.
    const elements: HTMLElement[] = []
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT)
    let n = walker.nextNode()
    while (n) {
        elements.push(n as HTMLElement)
        n = walker.nextNode()
    }
    for (const el of elements) {
        if (!ALLOWED_TAGS.has(el.tagName)) {
            unwrap(el)
            continue
        }
        // Retire tous les attributs, ne réintroduit que text-align si valide. L'attribut
        // HTML `align="center"` (collage, ancien navigateur) en est le repli : même règle
        // que le document (`sanitize_block_html`, keep_alignment) — le style l'emporte.
        const align = [el.style?.textAlign, el.getAttribute('align')?.trim().toLowerCase()]
            .find((v) => v && ALLOWED_ALIGN.has(v))
        for (const attr of Array.from(el.attributes)) {
            el.removeAttribute(attr.name)
        }
        if (align) {
            el.style.textAlign = align
        }
    }
}

/** Remplace un élément par ses enfants (conserve le texte, retire la balise). */
function unwrap(el: HTMLElement): void {
    const parent = el.parentNode
    if (!parent) return
    while (el.firstChild) parent.insertBefore(el.firstChild, el)
    parent.removeChild(el)
}
